import type { DataStore } from '@/storage';
import type { BusinessTemplate, ID, QuotePreset, Service, ServiceCategory } from '@/types';

export interface SeedResult {
  categories: ServiceCategory[];
  services: Service[];
  presets: QuotePreset[];
}

/**
 * Materialises a template's configuration into the owner's editable catalog.
 *
 * Configuration is the *seed*, not the source of truth: once written, the rows
 * belong to the business and can be renamed, repriced or deleted without ever
 * touching `templates/`. `definitionId` keeps the provenance link so a future
 * "restore defaults" can diff against configuration.
 */
export async function seedCatalog(
  store: DataStore,
  businessId: ID,
  template: BusinessTemplate,
): Promise<SeedResult> {
  const categories = await store.categories.createMany(
    [...template.categories]
      .sort((a, b) => a.order - b.order)
      .map((definition) => ({
        businessId,
        definitionId: definition.id,
        name: definition.name,
        icon: definition.icon,
        color: definition.color,
        order: definition.order,
      })),
  );

  const categoryIdByDefinition = new Map(
    categories.map((category) => [category.definitionId as string, category.id]),
  );

  const services = await store.services.createMany(
    template.services.flatMap((definition) => {
      const categoryId = categoryIdByDefinition.get(definition.categoryId);
      if (!categoryId) return [];

      return [
        {
          businessId,
          categoryId,
          definitionId: definition.id,
          name: definition.name,
          description: definition.description,
          price: definition.basePrice,
          unit: definition.unit,
          icon: definition.icon,
          color: definition.color,
          displayOrder: definition.displayOrder,
          tags: [...definition.tags],
          isTaxable: definition.isTaxable,
          isPopular: definition.isPopular,
          isFavourite: false,
          status: 'active' as const,
          usageCount: 0,
          keywords: [...(definition.keywords ?? [])],
          // The workbook's pricing model travels with the default it qualifies,
          // and from here on belongs to the owner exactly as the price does.
          ...(definition.pricingNote === undefined ? {} : { pricingNote: definition.pricingNote }),
        },
      ];
    }),
  );

  // Presets reference configuration service ids; remap them onto the rows we
  // just created so a bundle keeps working after the owner renames a service.
  const serviceIdByDefinition = new Map(
    services.map((service) => [service.definitionId as string, service.id]),
  );

  const presets = await store.presets.createMany(
    template.presets.flatMap((definition) => {
      const items = definition.items
        .map((presetItem) => {
          const serviceId = serviceIdByDefinition.get(presetItem.serviceId);
          return serviceId ? { serviceId, quantity: presetItem.quantity } : null;
        })
        .filter((presetItem): presetItem is { serviceId: ID; quantity: number } => presetItem !== null);

      if (items.length === 0) return [];

      return [
        {
          businessId,
          definitionId: definition.id,
          name: definition.name,
          description: definition.description,
          icon: definition.icon,
          color: definition.color,
          displayOrder: definition.displayOrder,
          items,
        },
      ];
    }),
  );

  return { categories, services, presets };
}
